import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionButton, Content, Dialog, DialogTrigger, Divider, Heading } from '@adobe/react-spectrum';

import { DynamoConnector, getUser } from '../../data/DynamoConnector';
import { setAppRoute } from '../../data/redux/slices/appSlice';
import { useOpenCardInfo } from '../common/cardInfoDialog/useOpenCardInfo';
import { getManaClass } from '../common/cardStats/manaText';
import '../common/cardStats/cardStats.css';
import '../common/searedDialog/searedDialog.css';
import '../filterpanel/mana-cost.css';
import './userStatsPanel.css';

// Fixed axis order — keeping this constant lets users compare radar
// silhouettes across profiles without re-orienting.
const AXES = [
    {
        key: 'timmy',
        label: 'Timmy / Tammy',
        short: 'Timmy',
        tagline: 'The Experiencer',
        tooltip: 'Plays Magic to feel it. Big creatures, epic swings, haymakers. Prefers the moment over the win.',
        kind: 'Psychographic',
        description:
            'Plays Magic to feel something. The highlight is the big moment — resolving the enormous creature, casting the haymaker, landing the game-warping spell. Splashy over efficient, memorable over consistent.',
        cues: 'High-CMC creatures, "build-around" cards, win-big swings.',
    },
    {
        key: 'johnny',
        label: 'Johnny / Jenny',
        short: 'Johnny',
        tagline: 'The Expressionist',
        tooltip: 'Treats deckbuilding as self-expression. Loves combos, engines, and making weird cards work together.',
        kind: 'Psychographic',
        description:
            'Plays Magic to express something. Deckbuilding is a canvas — the goal is to assemble a clever engine or a combo nobody else saw. Winning is validation that the idea worked, not the point itself.',
        cues: 'Combos, engine pieces, "make it work" tutors and enablers.',
    },
    {
        key: 'spike',
        label: 'Spike',
        short: 'Spike',
        tagline: 'The Competitor',
        tooltip: 'Plays to win. Tunes for efficiency and consistency. Tutors, fast mana, proven shells.',
        kind: 'Psychographic',
        description:
            'Plays to prove something. Looks for the correct answer and pilots it — will copy a proven list over a pet deck if it wins more. Efficiency, consistency, and a respect for the metagame define Spike.',
        cues: 'Fast mana, tutors, staple interaction, tuned meta shells.',
    },
    {
        key: 'vorthos',
        label: 'Vorthos',
        short: 'Vorthos',
        tagline: 'The Flavor Lover',
        tooltip: 'Picks cards for story, characters, and theme. Tribal decks, on-flavor builds, story over strategy.',
        kind: 'Aesthetic profile',
        description:
            'Loves Magic for the story, characters, art, and flavor text. A great card reads like a small scene from the world. Not a motivation for playing — a lens on what makes a card beautiful.',
        cues: 'Tribal themes, legendary-creature shells, on-flavor card picks.',
    },
    {
        key: 'mel',
        label: 'Mel / Melvin',
        short: 'Mel',
        tagline: 'The Mechanics Lover',
        tooltip: 'Loves the underlying system. Appreciates elegant rules interactions and mechanic-matters synergy.',
        kind: 'Aesthetic profile',
        description:
            'Vorthos\u2019s twin on the aesthetic axis — where Vorthos prizes flavor, Mel prizes mechanics. Appreciates the invisible machinery: the color pie, templating, rules interactions, clean design.',
        cues: 'Mechanic-matters synergy, rules-dense cards, keyword-soup decks.',
    },
];
const AXES_BY_KEY = AXES.reduce((acc, a) => ({ ...acc, [a.key]: a }), {});

// Canonical subtype lists for the info dialog. Matches Mark Rosewater's
// original axis sub-divisions ("Timmy, Johnny, Spike Revisited" / "Vorthos
// and Mel"). Keep these in sync with the backend's playerType.subtypes
// definitions — these render in the static info dialog; live scores are
// still computed from the API at runtime.
const AXIS_SUBTYPES = {
    timmy: [
        { label: 'Power Gamer', description: 'Wants big, powerful plays — the haymaker moment.' },
        { label: 'Social Gamer', description: 'Plays for the table interaction, not the victory.' },
        { label: 'Diversity Gamer', description: 'Every deck is different — exploration over optimization.' },
        { label: 'Adrenaline Gamer', description: 'Chases variance — wheels, storm, coin-flip chaos.' },
    ],
    johnny: [
        { label: 'Combo Player', description: 'Finds card combinations others miss.' },
        { label: 'Offbeat Designer', description: 'Proves an idea works by building it around unusual answers.' },
        { label: 'Uber Johnny', description: 'Makes the undoable work — no card too bad to build around.' },
        { label: 'Deck Artist', description: 'Deckbuilding as self-expression — tribal, theme, aesthetic.' },
    ],
    spike: [
        { label: 'Innovator', description: 'Finds the next broken deck — competitive across many shells.' },
        { label: 'Analyst', description: 'Picks the best deck for the meta — interaction-heavy, control-leaning.' },
        { label: 'Tuner', description: 'Min-maxer — takes a known shell and squeezes every percent.' },
        {
            label: 'Nuts & Bolts',
            description: 'Perfects pilot play — not really about deckbuilding.',
            undetectable: 'Needs game-log data (mulligans, lines of play) we do not track.',
        },
    ],
    vorthos: [
        { label: 'The Gamer', description: 'Top-down flavor — tribal themes and in-game resonance.' },
        { label: 'The Dreamer', description: 'Lore-driven — every deck a story, every commander a character.' },
        {
            label: 'The Artist',
            description: 'Values visual design — art, alters, border choices.',
            undetectable: 'Needs art / alter preference data outside the deck layer.',
        },
        {
            label: 'The Writer',
            description: 'Values flavor text and card names.',
            undetectable: 'Needs flavor-text engagement data we do not track.',
        },
        {
            label: 'The Oracle',
            description: 'Blends real and fantasy — cosplay, community, world engagement.',
            undetectable: 'Needs community / cosplay signals outside the deck layer.',
        },
    ],
    mel: [],
};

const CONFIDENCE_META = {
    high: { label: 'High confidence', tone: 'ok' },
    medium: { label: 'Medium confidence', tone: 'watch' },
    low: { label: 'Low confidence', tone: 'alert' },
};

const COLOR_META = {
    W: { label: 'White' },
    U: { label: 'Blue' },
    B: { label: 'Black' },
    R: { label: 'Red' },
    G: { label: 'Green' },
    C: { label: 'Colorless' },
};

const WUBRG = ['W', 'U', 'B', 'R', 'G', 'C'];

// Colors for the major/minor archetype breakdown bar and chips. Keys are
// uppercase bucket labels (COMBO, MIDRANGE, CONTROL, ...) so they match the
// API payload directly.
const ARCHETYPE_COLORS = {
    COMBO: '#e8c600',
    CONTROL: '#4a9bd6',
    MIDRANGE: '#d88a3a',
    AGGRO: '#cc1a1a',
    RAMP: '#5fa85a',
    STAX: '#9966cc',
    REANIMATOR: '#d04a6c',
    STOMPY: '#8aab4d',
    VOLTRON: '#d68c3a',
    TOKENS: '#5fa85a',
    TRIBAL: '#6b8e9c',
    STORM: '#e8c600',
    TOOLBOX: '#b89848',
    COMBAT: '#cc5a1a',
};

function archetypeColor(key) {
    return ARCHETYPE_COLORS[String(key || '').toUpperCase()] || '#8b93a7';
}

function prettifyArchetype(label) {
    if (!label) return '';
    // Leaf labels can be joint like "COMBO / CONTROL" — title-case each side.
    return String(label)
        .split('/')
        .map((part) => prettifyCardId(part.trim().toLowerCase()))
        .join(' / ');
}

// ---------- Salt profile constants ---------------------------------------
// Family / intensity / histogram palettes for the Salt section + hero line.
// Keys mirror SaltPersonalityCatalog on the backend; unknown keys fall
// through to prettifyCardId() for a humanized display.

const SALT_FAMILY_LABEL = {
    land_denial: 'Land denial',
    stax: 'Stax',
    boardwipe: 'Boardwipe',
    combo: 'Combo',
    wallet: 'Wallet',
    hand_hate: 'Hand hate',
    combat: 'Combat',
    theft: 'Theft',
    annihilator: 'Annihilator',
    pain: 'Pain',
    chaos: 'Chaos',
    poison: 'Poison',
    sacrifice: 'Sacrifice',
    tax_cost: 'Tax cost',
    meta: 'Meta density',
    mixed: 'Mixed',
};

const SALT_FAMILY_COLOR = {
    land_denial: '#8a5a2a',
    stax: '#9966cc',
    boardwipe: '#d6534a',
    combo: '#e8c600',
    wallet: '#d4a03a',
    hand_hate: '#6a5a8a',
    combat: '#cc5a1a',
    theft: '#6a3a6a',
    annihilator: '#cc1a1a',
    pain: '#d04a6c',
    chaos: '#d6534a',
    poison: '#5fa85a',
    sacrifice: '#8a4a8a',
    tax_cost: '#8a7a9e',
    meta: '#e8c600',
    mixed: '#8b93a7',
};

const SALT_INTENSITY_ORDER = ['mild', 'moderate', 'high', 'extreme'];
const SALT_INTENSITY_LABEL = { mild: 'Mild', moderate: 'Moderate', high: 'High', extreme: 'Extreme' };
const SALT_INTENSITY_COLOR = {
    mild: '#5fa85a',
    moderate: '#e8c600',
    high: '#e08a30',
    extreme: '#cc1a1a',
};
// Marker positions on the hero intensity gauge — the center of each
// quartile so the dot sits under its label in a 4-column scale.
const SALT_INTENSITY_POSITION = { mild: 12.5, moderate: 37.5, high: 62.5, extreme: 87.5 };

const SALT_HISTOGRAM_BUCKETS = [
    { key: 'salt_0_5', label: '0–5' },
    { key: 'salt_5_15', label: '5–15' },
    { key: 'salt_15_30', label: '15–30' },
    { key: 'salt_30_60', label: '30–60' },
    { key: 'salt_60_plus', label: '60+' },
];
const SALT_HISTOGRAM_COLOR = {
    salt_0_5: '#5fa85a',
    salt_5_15: '#a8c03a',
    salt_15_30: '#e8c600',
    salt_30_60: '#e08a30',
    salt_60_plus: '#cc1a1a',
};

function prettySaltFamily(key) {
    return SALT_FAMILY_LABEL[key] || prettifyCardId(key);
}

function saltFamilyColor(key) {
    return SALT_FAMILY_COLOR[key] || '#8b93a7';
}

// `personality.intensityCounts` omits zero keys; pick the top populated one.
// Returns null if no intensity bucket carries any decks.
function dominantSaltIntensity(intensityCounts) {
    if (!intensityCounts) return null;
    let best = null;
    let bestCount = 0;
    for (const k of SALT_INTENSITY_ORDER) {
        const v = Number(intensityCounts[k] || 0);
        if (v > bestCount) {
            best = k;
            bestCount = v;
        }
    }
    return best;
}

// Renders the official MTG mana sprite for a single color letter (W/U/B/R/G/C).
// Uses the same sprite set (`.mana.<size>.s<color>`) that rules-text rendering
// uses, so mana presentation stays consistent across the app.
function ManaSymbol({ color, size = 'medium' }) {
    const label = COLOR_META[color]?.label || color;
    return <span className={`mana ${size} ${getManaClass(color)} manaTextIcon`} aria-label={label} title={label} />;
}

function diversityBucket(v) {
    if (v == null) return { label: '—', tone: 'neutral' };
    if (v < 0.3) return { label: 'Narrow', tone: 'watch' };
    if (v <= 0.7) return { label: 'Mixed', tone: 'ok' };
    return { label: 'Broad', tone: 'alert' };
}

function formatRatio(v) {
    if (v == null || Number.isNaN(Number(v))) return '—';
    return `${Math.round(Number(v) * 100)}%`;
}

// The panel is viewable by anyone, not just the profile's owner. We
// switch between first-person and third-person copy so the hero and
// hints stay grammatical regardless of who's reading. When we know the
// profile name we use it as the subject; otherwise we fall back to a
// neutral "they/their".
function buildVoice(isSelf, profileName) {
    const name = profileName || '';
    if (isSelf) {
        return {
            heroEyebrow: 'You are a',
            confidenceNote: (count) => `Based on only ${count || 'a few'} decks — profile sharpens as you add more.`,
            evidenceHint: 'Signals from your decks that fed each axis. Heavier weight = stronger signal.',
            signatureHint: 'Cards appearing in at least 70% of your decks — the core that defines your style.',
            signatureEmpty: 'No signature cards yet — your decks are too diverse to share a common core.',
            commandersHint: 'Commanders you build around most. Click through for the deep-dive.',
            topCardsHint: 'The cards you reach for most across every deck you own.',
            topCombosHint: "Combo lines you've shipped most often. Click through to Commander Spellbook.",
            archetypeHint: 'How broad your deck palette is, and which strategies you build most.',
            colorsHint: 'Color identity distribution across your deck library.',
            aggregateHint: 'Averages across every deck you own — the raw feedstock the classifier reads.',
            saltHint: 'Your salt fingerprint — how much, what flavor, and the intensity behind it.',
            pendingBody:
                'Your play-style profile is still being computed. The pipeline ticks every ~15 minutes — check back shortly.',
            belowThresholdBody: (count) =>
                count
                    ? `You have ${count} deck${
                          count === 1 ? '' : 's'
                      } indexed — import more to unlock your play-style radar.`
                    : 'Import your first deck to unlock your play-style radar.',
            errorBody:
                'Couldn\u2019t reach the stats service just now. Your profile will pick up automatically on the next load.',
        };
    }
    const poss = name ? `${name}'s` : 'their';
    const builds = name ? `${name} builds` : 'they build';
    const reaches = name ? `${name} reaches` : 'they reach';
    const owns = name ? `${name} owns` : 'they own';
    const playedByName = name || 'this player';
    return {
        heroEyebrow: 'Plays like a',
        confidenceNote: (count) => `Based on only ${count || 'a few'} decks — profile sharpens as more are added.`,
        evidenceHint: `Signals from ${poss} decks that fed each axis. Heavier weight = stronger signal.`,
        signatureHint: `Cards appearing in at least 70% of ${poss} decks — the core that defines ${poss} style.`,
        signatureEmpty: `No signature cards yet — ${poss} decks are too diverse to share a common core.`,
        commandersHint: `Commanders ${builds} around most. Click through for the deep-dive.`,
        topCardsHint: `The cards ${reaches} for most across every deck ${owns}.`,
        topCombosHint: `Combo lines ${playedByName} has shipped most often. Click through to Commander Spellbook.`,
        archetypeHint: `How broad ${poss} deck palette is, and which strategies ${builds} most.`,
        colorsHint: `Color identity distribution across ${poss} deck library.`,
        aggregateHint: `Averages across every deck ${owns} — the raw feedstock the classifier reads.`,
        saltHint: `${
            playedByName[0].toUpperCase() + playedByName.slice(1)
        }'s salt fingerprint — how much, what flavor, and the intensity behind it.`,
        pendingBody: `${
            playedByName[0].toUpperCase() + playedByName.slice(1)
        }'s play-style profile is still being computed. The pipeline ticks every ~15 minutes — check back shortly.`,
        belowThresholdBody: (count) =>
            count
                ? `${playedByName[0].toUpperCase() + playedByName.slice(1)} has ${count} deck${
                      count === 1 ? '' : 's'
                  } indexed — more decks are needed to unlock the play-style radar.`
                : `${playedByName[0].toUpperCase() + playedByName.slice(1)} hasn\u2019t indexed any decks yet.`,
        errorBody:
            'Couldn\u2019t reach the stats service just now. The profile will pick up automatically on the next load.',
    };
}

// Turn a card id slug (`thassas_oracle`, `demonic-consultation`) into a
// human-readable card name. Best-effort title-case — handles the bulk of
// real cards cleanly; weird punctuation (apostrophes, commas, //-joined
// MDFCs) still rounds out to a reasonable display string.
function prettifyCardId(id) {
    if (!id) return '';
    return String(id)
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
        .join(' ');
}

// ---------- Root -----------------------------------------------------------

export function UserStatsPanel() {
    const profileId = useSelector((s) => s?.userDetails?.profile?.id);
    const profileName = useSelector((s) => s?.userDetails?.profile?.metadata?.profileName);
    // Profile can be viewed by anyone; if the viewer is the owner we use
    // first-person copy ("you are a Spike"), otherwise third-person
    // ("plays like a Spike"). `getUser()?.profile?.sub` matches the
    // Cognito subject that populates `profileId` on self-view.
    const loggedInSub = getUser()?.profile?.sub;
    const isSelf = !!(loggedInSub && profileId && loggedInSub === profileId);
    const voice = useMemo(() => buildVoice(isSelf, profileName), [isSelf, profileName]);

    const [envelope, setEnvelope] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    // Always look up by the UUID ownerId, not the username. The API
    // accepts either on paper but the stats bin is keyed by ownerId, so
    // hitting it with the UUID avoids an extra username → id lookup
    // hop on the server.
    const lookupId = profileId;

    useEffect(() => {
        if (!lookupId) return;
        let cancelled = false;
        setLoading(true);
        setError(false);
        DynamoConnector.getUserCardStats(lookupId, (data) => {
            if (cancelled) return;
            if (!data) {
                setError(true);
                setEnvelope(null);
            } else {
                setEnvelope(data);
            }
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [lookupId]);

    const status = envelope?.status || (loading ? 'loading' : error ? 'error' : 'unknown');
    const playerType = envelope?.playerType;
    const projection = envelope?.projection;

    // Status-driven rendering per §6.1 of the spec.
    if (!envelope && loading) return <StatusPanel variant="loading" voice={voice} />;
    if (!envelope && error) return <StatusPanel variant="error" voice={voice} />;
    if (!envelope) return <StatusPanel variant="loading" voice={voice} />;
    if (status === 'pending') return <StatusPanel variant="pending" voice={voice} />;
    if (status === 'below_threshold')
        return <StatusPanel variant="below_threshold" voice={voice} deckCount={envelope?.deckCount} />;
    if (!playerType || !playerType.scores) return <StatusPanel variant="pending" voice={voice} />;
    const hasScores = Object.values(playerType.scores).some((v) => v > 0);
    if (!hasScores) return <StatusPanel variant="pending" voice={voice} />;

    return (
        <div className="userStatsPanel userStatsPanelReady">
            <ProfileHero
                playerType={playerType}
                envelope={envelope}
                profileName={profileName}
                voice={voice}
                salt={projection?.salt}
            />

            <section className="userStatsPanelRadarRow">
                <header className="userStatsPlayerTypeHead">
                    <div className="userStatsPlayerTypeHeadLeft">
                        <span className="userStatsPlayerTypeEyebrow">Player type</span>
                        <span className="userStatsPlayerTypeHint">
                            Five axes of deckbuilding style — Timmy, Johnny, Spike, Vorthos, Mel. New here?
                        </span>
                    </div>
                    <PlayerTypeInfoDialog />
                </header>
                <div className="userStatsPanelRadarGrid">
                    <RadarChart scores={playerType.scores} />
                    <ScoreLegend
                        scores={playerType.scores}
                        primary={playerType.primary}
                        secondary={playerType.secondary}
                        subtypes={playerType.subtypes}
                    />
                </div>
            </section>

            <EvidenceSection
                evidence={playerType.evidence || []}
                primary={playerType.primary}
                secondary={playerType.secondary}
                voice={voice}
            />

            <SaltSection salt={projection?.salt} voice={voice} />

            <ArchetypeSection
                majorArchetypes={projection?.majorArchetypes || []}
                minorArchetypes={projection?.minorArchetypes || []}
                topArchetypes={projection?.topArchetypes || []}
                diversity={projection?.archetypeDiversity ?? playerType?.aggregate?.archetypeDiversity}
                voice={voice}
            />

            <ColorProfileSection
                combinations={projection?.colorCombinations || []}
                perColor={projection?.perColorCounts || {}}
                deckCount={envelope?.deckCount}
                voice={voice}
            />

            <div className="userStatsPanelSplit">
                <SignatureCardsSection cards={projection?.signatureCards || []} voice={voice} />
                <TopCommandersSection commanders={projection?.topCommanders || []} voice={voice} />
            </div>

            <div className="userStatsPanelSplit">
                <TopCardsSection cards={projection?.topCards || []} voice={voice} />
                <TopCombosSection combos={projection?.topCombos || []} voice={voice} />
            </div>

            <CategoryRoleSection roles={projection?.categoryRolesPerDeck} subs={projection?.categorySubsPerDeck} />

            <AggregateStats
                aggregate={playerType?.aggregate}
                projection={projection}
                lastUpdatedAt={envelope?.lastUpdatedAt}
                voice={voice}
            />
        </div>
    );
}

// ---------- Status placeholders -------------------------------------------

function StatusPanel({ variant, deckCount, voice }) {
    const v = voice || buildVoice(false, null);
    const COPY = {
        loading: { label: 'Computing', body: 'Pulling together the deck aggregates…' },
        error: { label: 'Offline', body: v.errorBody },
        pending: { label: 'Data pending', body: v.pendingBody },
        below_threshold: {
            label: 'Not enough decks yet',
            body: v.belowThresholdBody(deckCount),
        },
    };
    const copy = COPY[variant] || COPY.pending;
    return (
        <div className="userStatsPanel">
            <header className="userStatsPanelHeader">
                <span className="userStatsPanelEyebrow">Play-style profile</span>
                <span className="userStatsPanelHint">
                    A five-axis read of deckbuilding style — Timmy, Johnny, Spike, Vorthos, Mel.
                </span>
            </header>
            <div className="userStatsPanelPlaceholder" role="status" aria-busy={variant === 'loading'}>
                <span className="userStatsPanelPlaceholderLabel">{copy.label}</span>
                <span className="userStatsPanelPlaceholderSub">{copy.body}</span>
            </div>
        </div>
    );
}

// ---------- Hero ----------------------------------------------------------

// Horizontal salt-level gauge under the hero salt line — a continuous
// mild→extreme gradient with a marker pinned at the user's dominant
// intensity bucket. The hero text already names the intensity
// ("featuring Extreme levels of salty Stax"), so this is a pure
// visual gauge — no floating label on the marker.
function SaltIntensityGauge({ dominant, deckCount }) {
    const left = SALT_INTENSITY_POSITION[dominant] ?? 50;
    const accent = SALT_INTENSITY_COLOR[dominant] || '#e8c600';
    const label = SALT_INTENSITY_LABEL[dominant] || '';
    return (
        <div className="userStatsHeroSaltGauge" title={`${label} intensity across ${deckCount || '—'} decks`}>
            <div className="userStatsHeroSaltGaugeTrack">
                <div className="userStatsHeroSaltGaugeMarker" style={{ left: `${left}%`, color: accent }} />
            </div>
            <div className="userStatsHeroSaltGaugeScale">
                {SALT_INTENSITY_ORDER.map((k) => (
                    <span
                        key={k}
                        className={`userStatsHeroSaltGaugeTick${
                            k === dominant ? ' userStatsHeroSaltGaugeTick--active' : ''
                        }`}
                        style={k === dominant ? { color: SALT_INTENSITY_COLOR[k] } : undefined}
                    >
                        {SALT_INTENSITY_LABEL[k]}
                    </span>
                ))}
            </div>
        </div>
    );
}

function ProfileHero({ playerType, envelope, profileName, voice, salt }) {
    const primary = playerType.primary;
    const secondary = playerType.secondary;
    const primaryAxis = primary ? AXES_BY_KEY[primary] : null;
    const secondaryAxis = secondary ? AXES_BY_KEY[secondary] : null;
    const confidence = CONFIDENCE_META[playerType.confidence] || {
        label: 'Unknown',
        tone: 'neutral',
    };
    const deckCount = envelope?.deckCount;
    const isStale = envelope?.status === 'stale';

    // Fold the dominant salt family + intensity into the hero's own
    // grammar — styled like the Johnny-streak subtitle so it reads as part
    // of the same identity sentence rather than a separate chip.
    const personality = salt?.personality;
    const primaryFamily = personality?.primaryFamilies?.[0]?.key;
    const familyLabel = primaryFamily ? prettySaltFamily(primaryFamily) : null;
    const dominantInt = dominantSaltIntensity(personality?.intensityCounts);

    return (
        <section className="userStatsHero">
            <div className="userStatsHeroMain">
                <span className="userStatsHeroEyebrow">{voice.heroEyebrow}</span>
                <div className="userStatsHeroTitleRow">
                    <span className="userStatsHeroTitle">{primaryAxis?.short || 'Mystery'}</span>
                    {primaryAxis && <span className="userStatsHeroTagline">{primaryAxis.tagline}</span>}
                </div>
                {secondaryAxis && (
                    <span className="userStatsHeroSecondary">
                        with a <strong>{secondaryAxis.short}</strong> streak
                    </span>
                )}
                {familyLabel && (
                    <>
                        <div
                            className="userStatsHeroSalt"
                            title={
                                dominantInt
                                    ? `${SALT_INTENSITY_LABEL[dominantInt]} intensity across ${deckCount || '—'} decks`
                                    : undefined
                            }
                        >
                            <span className="userStatsHeroSaltLead">featuring</span>{' '}
                            {dominantInt && (
                                <>
                                    <strong
                                        className="userStatsHeroSaltIntensity"
                                        style={{ color: SALT_INTENSITY_COLOR[dominantInt] }}
                                    >
                                        {SALT_INTENSITY_LABEL[dominantInt]}
                                    </strong>
                                    <span className="userStatsHeroSaltMiddle"> levels of </span>
                                </>
                            )}
                            <span className="userStatsHeroSaltWord">salty </span>
                            <strong className="userStatsHeroSaltFamily">{familyLabel}</strong>
                        </div>
                        {dominantInt && <SaltIntensityGauge dominant={dominantInt} deckCount={deckCount} />}
                    </>
                )}
            </div>
            <div className="userStatsHeroMeta">
                <div className="userStatsHeroMetaTile">
                    <span className="userStatsHeroMetaLabel">Decks analyzed</span>
                    <span className="userStatsHeroMetaValue">{deckCount ?? '—'}</span>
                </div>
                <div className="userStatsHeroBadges">
                    <span
                        className={`userStatsHeroConfidence userStatsHeroConfidence--${confidence.tone}`}
                        title={playerType.confidence === 'low' ? 'Profile sharpens as more decks are added' : undefined}
                    >
                        {confidence.label}
                    </span>
                    {isStale && (
                        <span
                            className="userStatsHeroConfidence userStatsHeroConfidence--watch"
                            title="A fresh rebuild is in flight. Numbers will update on the next fetch."
                        >
                            Refreshing…
                        </span>
                    )}
                </div>
                {profileName && <span className="userStatsHeroName">@{profileName}</span>}
            </div>
            {playerType.confidence === 'low' && (
                <span className="userStatsHeroConfidenceNote">{voice.confidenceNote(deckCount)}</span>
            )}
        </section>
    );
}

// ---------- Player-type info dialog --------------------------------------

// Renders the static subtype list inside the info dialog card. Mel
// explicitly has no canonical subtypes — we surface that as a muted
// note rather than an empty block.
function AxisSubtypeList({ axisKey }) {
    const subtypes = AXIS_SUBTYPES[axisKey] || [];
    if (subtypes.length === 0) {
        if (axisKey !== 'mel') return null;
        return (
            <span className="userStatsInfoCardSubtypesEmpty">
                Mel has no canonical subtypes in WotC&rsquo;s framework.
            </span>
        );
    }
    return (
        <div className="userStatsInfoCardSubtypes">
            <span className="userStatsInfoCardSubtypesLabel">Subtypes</span>
            <ul className="userStatsInfoCardSubtypesList">
                {subtypes.map((s) => (
                    <li
                        key={s.label}
                        className={`userStatsInfoCardSubtype${
                            s.undetectable ? ' userStatsInfoCardSubtype--muted' : ''
                        }`}
                    >
                        <span className="userStatsInfoCardSubtypeName">{s.label}</span>
                        <span className="userStatsInfoCardSubtypeDesc">
                            {s.description}
                            {s.undetectable ? (
                                <em className="userStatsInfoCardSubtypeNa"> (n/a — {s.undetectable})</em>
                            ) : null}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function PlayerTypeInfoDialog() {
    return (
        <DialogTrigger isDismissable>
            <ActionButton
                aria-label="Explain the five player-type axes"
                UNSAFE_className="userStatsInfoButton searedBtn"
            >
                <span className="userStatsInfoIcon" aria-hidden="true">
                    ?
                </span>
                <span className="userStatsInfoGlyph">What does this mean?</span>
            </ActionButton>
            {(close) => (
                <Dialog
                    size="L"
                    padding={0}
                    width="calc(100vw - 40px)"
                    maxWidth={720}
                    UNSAFE_className="searedDialog userStatsInfoDialog"
                >
                    <Heading>
                        <span className="searedDialog__eyebrow">About</span>
                        <span className="searedDialog__title">Player types</span>
                        <span className="searedDialog__subtitle">
                            Mark Rosewater&rsquo;s five axes, adapted for deckbuilding
                        </span>
                    </Heading>
                    <Divider />
                    <Content UNSAFE_className="userStatsInfoContent">
                        <div className="searedDialog__body">
                            <p className="searedDialog__paragraph">
                                Few players are pure anything — these five axes give a rough read of deckbuilding style.{' '}
                                <strong>Timmy, Johnny, and Spike</strong> are <em>psychographics</em> (what motivates a
                                player) and <strong>Vorthos and Mel</strong> are <em>aesthetic profiles</em> (what a
                                player finds beautiful about the game).
                            </p>
                            <div className="userStatsInfoGrid">
                                {AXES.map((axis) => (
                                    <div key={axis.key} className="userStatsInfoCard">
                                        <div className="userStatsInfoCardHead">
                                            <span className="userStatsInfoCardName">{axis.label}</span>
                                            <span className="userStatsInfoCardKind">{axis.kind}</span>
                                        </div>
                                        <span className="userStatsInfoCardTagline">{axis.tagline}</span>
                                        <p className="userStatsInfoCardBody">{axis.description}</p>
                                        <span className="userStatsInfoCardCues">
                                            <strong>Signals:</strong> {axis.cues}
                                        </span>
                                        <AxisSubtypeList axisKey={axis.key} />
                                    </div>
                                ))}
                            </div>
                            <div className="searedDialog__legal">
                                Adapted from Mark Rosewater&rsquo;s{' '}
                                <a
                                    className="userStatsInfoSourceLink"
                                    href="https://magic.wizards.com/en/news/making-magic/timmy-johnny-and-spike-revisited-2006-03-20"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    &ldquo;Timmy, Johnny, and Spike Revisited&rdquo; (2006)
                                </a>{' '}
                                and{' '}
                                <a
                                    className="userStatsInfoSourceLink"
                                    href="https://magic.wizards.com/en/news/making-magic/vorthos-and-mel-2015-08-31"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    &ldquo;Vorthos and Mel&rdquo; (2015)
                                </a>{' '}
                                on magic.wizards.com, with cross-reference to the{' '}
                                <a
                                    className="userStatsInfoSourceLink"
                                    href="https://mtg.fandom.com/wiki/Player_type"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    MTG Wiki player-type article
                                </a>
                                . Many players sit across several axes — a radar silhouette is a snapshot, not a label.
                            </div>
                        </div>
                    </Content>
                </Dialog>
            )}
        </DialogTrigger>
    );
}

// ---------- Radar chart (SVG) --------------------------------------------

const RADAR_SIZE = 300;
const RADAR_PADDING = 56;
const RADAR_R = (RADAR_SIZE - RADAR_PADDING * 2) / 2;
const RADAR_CX = RADAR_SIZE / 2;
const RADAR_CY = RADAR_SIZE / 2;

function axisAngle(idx) {
    return -Math.PI / 2 + (idx * 2 * Math.PI) / AXES.length;
}

function axisPoint(idx, ratio) {
    const a = axisAngle(idx);
    const r = RADAR_R * ratio;
    return { x: RADAR_CX + Math.cos(a) * r, y: RADAR_CY + Math.sin(a) * r };
}

function labelPoint(idx) {
    const a = axisAngle(idx);
    const r = RADAR_R + 22;
    return {
        x: RADAR_CX + Math.cos(a) * r,
        y: RADAR_CY + Math.sin(a) * r,
        anchor: Math.abs(Math.cos(a)) < 0.25 ? 'middle' : Math.cos(a) > 0 ? 'start' : 'end',
        baseline: Math.sin(a) < -0.25 ? 'text-after-edge' : Math.sin(a) > 0.25 ? 'hanging' : 'middle',
    };
}

function RadarChart({ scores }) {
    const normalized = AXES.map((a) => Math.max(0, Math.min(100, Number(scores?.[a.key] || 0))));
    const polygonPts = normalized
        .map((v, idx) => {
            const p = axisPoint(idx, v / 100);
            return `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
        })
        .join(' ');

    return (
        <div className="userStatsRadarWrap">
            <svg
                className="userStatsRadar"
                viewBox={`0 0 ${RADAR_SIZE} ${RADAR_SIZE}`}
                role="img"
                aria-label="Five-axis player-type profile"
            >
                {[0.25, 0.5, 0.75, 1].map((r) => (
                    <polygon
                        key={r}
                        className={`userStatsRadarRing userStatsRadarRing--${String(r).replace('0.', '')}`}
                        points={AXES.map((_, idx) => {
                            const p = axisPoint(idx, r);
                            return `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
                        }).join(' ')}
                    />
                ))}
                {AXES.map((_, idx) => {
                    const p = axisPoint(idx, 1);
                    return (
                        <line
                            key={`spoke-${idx}`}
                            className="userStatsRadarSpoke"
                            x1={RADAR_CX}
                            y1={RADAR_CY}
                            x2={p.x}
                            y2={p.y}
                        />
                    );
                })}
                <polygon className="userStatsRadarFill" points={polygonPts} />
                <polygon className="userStatsRadarStroke" points={polygonPts} />
                {normalized.map((v, idx) => {
                    const p = axisPoint(idx, v / 100);
                    return <circle key={`dot-${idx}`} className="userStatsRadarDot" cx={p.x} cy={p.y} r="4" />;
                })}
                {AXES.map((a, idx) => {
                    const lp = labelPoint(idx);
                    return (
                        <g key={a.key}>
                            <title>{a.tooltip}</title>
                            <text
                                x={lp.x}
                                y={lp.y}
                                className="userStatsRadarLabel"
                                textAnchor={lp.anchor}
                                dominantBaseline={lp.baseline}
                            >
                                {a.short}
                            </text>
                            <text
                                x={lp.x}
                                y={lp.y + 12}
                                className="userStatsRadarLabelScore"
                                textAnchor={lp.anchor}
                                dominantBaseline={lp.baseline}
                            >
                                {Math.round(normalized[idx])}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

function ScoreLegend({ scores, primary, secondary, subtypes }) {
    return (
        <div className="userStatsScoreLegend">
            {AXES.map((a) => {
                const v = Math.max(0, Math.min(100, Number(scores?.[a.key] || 0)));
                const tone = a.key === primary ? 'primary' : a.key === secondary ? 'secondary' : 'default';
                const axisSubtypes = subtypes?.[a.key];
                return (
                    <div key={a.key} className={`userStatsScoreRow userStatsScoreRow--${tone}`} title={a.tooltip}>
                        <div className="userStatsScoreMain">
                            <span className="userStatsScoreLabel">{a.label}</span>
                            <span className="userStatsScoreTagline">{a.tagline}</span>
                        </div>
                        <div className="userStatsScoreBarWrap">
                            <div className="userStatsScoreBar">
                                <div className="userStatsScoreBarFill" style={{ width: `${Math.max(2, v)}%` }} />
                            </div>
                            <span className="userStatsScoreValue">{Math.round(v)}</span>
                        </div>
                        <SubtypeBreakdown axisKey={a.key} data={axisSubtypes} />
                    </div>
                );
            })}
        </div>
    );
}

// Render the subtype row under an axis. The API may send:
//  - a populated `subtypes` map (most parents),
//  - an empty map with a top-level `note` (mel: "no canonical subtypes"),
//  - or undefined (pre-rollout data shapes).
// Detectable subtypes render as chips with score bars; undetectable
// subtypes render as muted "n/a" tiles with the `note` as a tooltip.
function SubtypeBreakdown({ axisKey, data }) {
    if (!data) return null;
    const entries = Object.entries(data.subtypes || {});
    // If the parent declares a top-level note and has no subtypes (e.g. Mel),
    // surface the note so the row doesn't look broken.
    if (entries.length === 0) {
        if (!data.note) return null;
        return (
            <div className="userStatsSubtypeRow userStatsSubtypeRow--empty">
                <span className="userStatsSubtypeNote">{data.note}</span>
            </div>
        );
    }
    // Sort: detectable with higher score first, then undetectable at the end.
    entries.sort((aPair, bPair) => {
        const [, a] = aPair;
        const [, b] = bPair;
        if (a.detectable !== b.detectable) return a.detectable ? -1 : 1;
        return (b.score ?? -1) - (a.score ?? -1);
    });
    const primaryKey = data.primary;
    const secondaryKey = data.secondary;
    return (
        <div className="userStatsSubtypeRow">
            {entries.map(([key, s]) => {
                const tone =
                    key === primaryKey
                        ? 'primary'
                        : key === secondaryKey
                        ? 'secondary'
                        : s.detectable
                        ? 'default'
                        : 'muted';
                const score = Number(s.score ?? 0);
                return (
                    <span
                        key={`${axisKey}-${key}`}
                        className={`userStatsSubtypeChip userStatsSubtypeChip--${tone}`}
                        title={s.description || s.note || s.label}
                    >
                        <span className="userStatsSubtypeChipLabel">{s.label}</span>
                        {s.detectable ? (
                            <span className="userStatsSubtypeChipValue">{Math.round(score)}</span>
                        ) : (
                            <span className="userStatsSubtypeChipValue userStatsSubtypeChipValue--na" title={s.note}>
                                n/a
                            </span>
                        )}
                    </span>
                );
            })}
        </div>
    );
}

// ---------- Evidence ------------------------------------------------------

function EvidenceSection({ evidence, primary, secondary, voice }) {
    const grouped = useMemo(() => groupEvidence(evidence, primary, secondary), [evidence, primary, secondary]);
    if (!grouped.length) return null;
    return (
        <section className="userStatsSection">
            <header className="userStatsSectionHeader">
                <span className="userStatsSectionEyebrow">Why?</span>
                <span className="userStatsSectionHint">{voice.evidenceHint}</span>
            </header>
            <div className="userStatsEvidenceGroups">
                {grouped.map((group) => (
                    <EvidenceGroup key={group.type} group={group} />
                ))}
            </div>
        </section>
    );
}

function groupEvidence(evidence, primary, secondary) {
    const byType = new Map();
    for (const entry of evidence || []) {
        const key = entry?.type || 'other';
        const arr = byType.get(key) || [];
        arr.push(entry);
        byType.set(key, arr);
    }
    const order = [primary, secondary, ...AXES.map((a) => a.key).filter((k) => k !== primary && k !== secondary)];
    const result = [];
    for (const key of order) {
        if (!key) continue;
        const entries = byType.get(key);
        if (!entries) continue;
        entries.sort((a, b) => (b.weight || 0) - (a.weight || 0));
        result.push({ type: key, entries });
    }
    return result;
}

function EvidenceGroup({ group }) {
    const axis = AXES_BY_KEY[group.type];
    return (
        <div className={`userStatsEvidenceGroup userStatsEvidenceGroup--${group.type}`}>
            <div className="userStatsEvidenceGroupHead">
                <span className="userStatsEvidenceGroupLabel">{axis?.short || group.type}</span>
                {axis?.tagline && <span className="userStatsEvidenceGroupTagline">{axis.tagline}</span>}
            </div>
            <ul className="userStatsEvidenceList">
                {group.entries.map((e, idx) => (
                    <li className="userStatsEvidenceItem" key={`${e.signal}_${idx}`}>
                        <div className="userStatsEvidenceRowTop">
                            <span className="userStatsEvidenceSignal">{e.signal}</span>
                            <span className="userStatsEvidenceWeight" title={`Weight: ${e.weight}`}>
                                +{Math.round(Number(e.weight || 0))}
                            </span>
                        </div>
                        {e.detail && <span className="userStatsEvidenceDetail">{e.detail}</span>}
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ---------- Salt profile -------------------------------------------------
//
// Renders salt metrics + personality from `projection.salt` in the user
// panel's visual vocabulary (eyebrow + tiled sub-blocks, not the commander
// SaltTab's gradient hero). Layout: aggregate tiles → distribution bar →
// personality (intensity strip + chip rows).

function SaltSection({ salt, voice }) {
    if (!salt) return null;
    const deckCount = Number(salt.deckCount || 0);
    const saltedDecks = Number(salt.decksWithSalt || 0);
    // `projection.salt` can be populated with a 0-salted corpus (legacy
    // bins, hydration misses). Nothing meaningful to render in that case.
    if (deckCount === 0 || saltedDecks === 0) return null;

    const personality = salt.personality;

    const saltedRatioPct =
        salt.decksWithSaltRatio != null ? `${Math.round(Number(salt.decksWithSaltRatio) * 100)}%` : null;
    const tiles = [
        { key: 'avgTotal', label: 'Avg salt / deck', value: salt.avgTotal, fmt: (v) => v.toFixed(1) },
        {
            key: 'avgTotalAmongSalted',
            label: 'When salty',
            value: salt.avgTotalAmongSalted,
            fmt: (v) => `+${v.toFixed(1)}`,
            hint: 'Average across only the decks that actually score salt',
        },
        {
            key: 'stddevTotal',
            label: 'σ spread',
            value: salt.stddevTotal,
            fmt: (v) => v.toFixed(1),
            hint: 'Standard deviation across decks — high = mix of chill and brutal lists',
        },
        {
            key: 'maxTotal',
            label: 'Hottest deck',
            value: salt.maxTotal,
            fmt: (v) => v.toFixed(1),
            hint: 'Highest single-deck salt total in the corpus',
        },
        {
            key: 'salted',
            label: 'Salty decks',
            value: saltedDecks,
            fmt: () => `${saltedDecks} / ${deckCount}`,
            sub: saltedRatioPct,
        },
        {
            key: 'avgMaxCategoryScore',
            label: 'Top-lane avg',
            value: salt.avgMaxCategoryScore,
            fmt: (v) => v.toFixed(1),
            hint: 'Average score of each deck’s dominant salt category',
        },
    ].filter((t) => t.value != null && !Number.isNaN(Number(t.value)));

    return (
        <section className="userStatsSection userStatsSalt">
            <header className="userStatsSectionHeader">
                <span className="userStatsSectionEyebrow">Salt profile</span>
                <span className="userStatsSectionHint">{voice.saltHint}</span>
            </header>

            <div className="userStatsAggregateGrid userStatsSaltTileGrid">
                {tiles.map((t) => (
                    <div key={t.key} className="userStatsAggregateTile userStatsSaltTile" title={t.hint || undefined}>
                        <span className="userStatsAggregateLabel">{t.label}</span>
                        <span className="userStatsAggregateValue">{t.fmt(Number(t.value))}</span>
                        {t.sub && <span className="userStatsSaltTileSub">{t.sub}</span>}
                    </div>
                ))}
            </div>

            <SaltHistogram histogram={salt.totalHistogram} deckCount={deckCount} />

            {personality && <SaltPersonalityBlock personality={personality} deckCount={deckCount} />}
        </section>
    );
}

function SaltHistogram({ histogram, deckCount }) {
    // User payload ships raw counts; compute the rate client-side. Use
    // deckCount as the denominator so all buckets add to ≤ 100% even if
    // a future bucket is added we don't know about yet.
    const denom = deckCount || 1;
    const entries = SALT_HISTOGRAM_BUCKETS.map((b) => {
        const count = Number(histogram?.[b.key] || 0);
        return { ...b, count, pct: denom > 0 ? count / denom : 0 };
    });
    const hasAny = entries.some((e) => e.count > 0);
    if (!hasAny) return null;

    return (
        <div className="userStatsSaltHistogram">
            <div className="userStatsSaltHistogramHead">
                <span className="userStatsSaltHistogramLabel">Salt distribution</span>
                <span className="userStatsSaltHistogramHint">
                    Where decks land on the salt scale — 0–5 is chill, 60+ is brutal.
                </span>
            </div>
            <div className="userStatsSaltHistogramBar">
                {entries.map((e) => {
                    if (e.count === 0) return null;
                    return (
                        <div
                            key={e.key}
                            className="userStatsSaltHistogramSegment"
                            style={{
                                width: `${Math.max(e.pct * 100, 1)}%`,
                                background: SALT_HISTOGRAM_COLOR[e.key],
                            }}
                            title={`${e.label} salt — ${e.count} deck${e.count === 1 ? '' : 's'} (${Math.round(
                                e.pct * 100
                            )}%)`}
                        />
                    );
                })}
            </div>
            <div className="userStatsSaltHistogramLegend">
                {entries.map((e) => (
                    <div key={e.key} className="userStatsSaltHistogramLegendItem">
                        <span
                            className="userStatsSaltHistogramSwatch"
                            style={{ background: SALT_HISTOGRAM_COLOR[e.key] }}
                        />
                        <span className="userStatsSaltHistogramLegendLabel">{e.label}</span>
                        <span className="userStatsSaltHistogramLegendValue">{e.count}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SaltPersonalityBlock({ personality, deckCount }) {
    const intensityCounts = personality?.intensityCounts || {};
    const intensityTotal = SALT_INTENSITY_ORDER.reduce((s, k) => s + Number(intensityCounts[k] || 0), 0);
    const denom = intensityTotal || deckCount || 1;

    return (
        <div className="userStatsSaltPersonality">
            <div className="userStatsSaltPersonalityHead">
                <span className="userStatsSaltPersonalityLabel">Salt personality</span>
                <span className="userStatsSaltPersonalityHint">
                    Per-deck intensity, plus the archetype and flavor tags that keep surfacing.
                </span>
            </div>
            {intensityTotal > 0 && (
                <div className="userStatsSaltIntensityStrip">
                    {SALT_INTENSITY_ORDER.map((k) => {
                        const count = Number(intensityCounts[k] || 0);
                        if (count <= 0) return null;
                        const ratio = count / denom;
                        return (
                            <div
                                key={k}
                                className="userStatsSaltIntensitySegment"
                                style={{
                                    width: `${Math.max(ratio * 100, 8)}%`,
                                    background: SALT_INTENSITY_COLOR[k],
                                }}
                                title={`${SALT_INTENSITY_LABEL[k]} — ${count} deck${
                                    count === 1 ? '' : 's'
                                } (${Math.round(ratio * 100)}%)`}
                            >
                                <span className="userStatsSaltIntensityLabel">{SALT_INTENSITY_LABEL[k]}</span>
                                <span className="userStatsSaltIntensityValue">{Math.round(ratio * 100)}%</span>
                            </div>
                        );
                    })}
                </div>
            )}
            <SaltChipRow
                label="Families"
                entries={personality?.primaryFamilies}
                format={prettySaltFamily}
                tint={saltFamilyColor}
            />
            <SaltChipRow label="Primary archetypes" entries={personality?.primaryArchetypes} format={prettifyCardId} />
            <SaltChipRow
                label="Secondary archetypes"
                entries={personality?.secondaryArchetypes}
                format={prettifyCardId}
                muted
            />
            <SaltChipRow label="Flavors" entries={personality?.flavors} format={prettifyCardId} />
        </div>
    );
}

function SaltChipRow({ label, entries, format, tint, muted }) {
    if (!Array.isArray(entries) || entries.length === 0) return null;
    const visible = entries.slice(0, 8);
    return (
        <div className="userStatsArchetypeGroup userStatsSaltChipGroup">
            <span className="userStatsArchetypeGroupLabel">{label}</span>
            <div className="userStatsArchetypeChips">
                {visible.map((e) => {
                    const pct = Math.round(Number(e?.deckRatio || 0) * 100);
                    const borderColor = tint ? tint(e.key) : undefined;
                    const className = `userStatsArchetypeChip${muted ? ' userStatsArchetypeChip--minor' : ''}`;
                    return (
                        <span
                            key={e.key}
                            className={className}
                            style={borderColor ? { borderLeftColor: borderColor } : undefined}
                            title={`${e.count || 0} deck${(e.count || 0) === 1 ? '' : 's'}`}
                        >
                            <span className="userStatsArchetypeChipLabel">{format(e.key)}</span>
                            <span
                                className={`userStatsArchetypeChipCount${
                                    muted ? ' userStatsArchetypeChipCount--minor' : ''
                                }`}
                            >
                                {pct}%
                            </span>
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

// ---------- Signature cards ----------------------------------------------

function SignatureCardsSection({ cards, voice }) {
    const openCardInfo = useOpenCardInfo();
    return (
        <section className="userStatsSection userStatsSignature">
            <header className="userStatsSectionHeader">
                <span className="userStatsSectionEyebrow">Signature cards</span>
                <span className="userStatsSectionHint">{voice.signatureHint}</span>
            </header>
            {cards.length === 0 ? (
                <div className="userStatsEmpty">{voice.signatureEmpty}</div>
            ) : (
                <div className="userStatsSignatureGrid">
                    {cards.map((c) => (
                        <button
                            key={c.cardId}
                            type="button"
                            className="userStatsSigCard"
                            onClick={() => openCardInfo({ id: c.cardId, name: c.name || c.cardId })}
                            title={`Appears in ${Math.round((c.deckRatio || 0) * 100)}% of decks`}
                        >
                            <span className="userStatsSigCardName">{c.name || c.cardId}</span>
                            <span className="userStatsSigCardMeta">
                                <span className="userStatsSigCardRatio">{Math.round((c.deckRatio || 0) * 100)}%</span>
                                <span className="userStatsSigCardCount">
                                    {c.count} deck{c.count === 1 ? '' : 's'}
                                </span>
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </section>
    );
}

// ---------- Top cards (new, full top-25) ---------------------------------

function TopCardsSection({ cards, voice }) {
    const openCardInfo = useOpenCardInfo();
    if (!cards.length) return null;
    return (
        <section className="userStatsSection">
            <header className="userStatsSectionHeader">
                <span className="userStatsSectionEyebrow">Top cards</span>
                <span className="userStatsSectionHint">{voice.topCardsHint}</span>
            </header>
            <ol className="userStatsTopCardList">
                {cards.map((c, idx) => (
                    <li key={c.cardId} className="userStatsTopCardRow">
                        <span className="userStatsTopCardRank">{idx + 1}</span>
                        <button
                            type="button"
                            className="userStatsTopCardBtn searedBtn"
                            onClick={() => openCardInfo({ id: c.cardId, name: c.name || c.cardId })}
                        >
                            <span className="userStatsTopCardName">{c.name || c.cardId}</span>
                        </button>
                        <div className="userStatsTopCardBar">
                            <div
                                className="userStatsTopCardBarFill"
                                style={{ width: `${Math.max(4, (c.deckRatio || 0) * 100)}%` }}
                            />
                        </div>
                        <span className="userStatsTopCardRatio">{Math.round((c.deckRatio || 0) * 100)}%</span>
                    </li>
                ))}
            </ol>
        </section>
    );
}

// ---------- Top combos (new) ---------------------------------------------

function TopCombosSection({ combos, voice }) {
    const repeatCombos = (combos || []).filter((c) => Number(c?.count) > 1);
    return (
        <section className="userStatsSection">
            <header className="userStatsSectionHeader">
                <span className="userStatsSectionEyebrow">Top combos</span>
                <span className="userStatsSectionHint">{voice.topCombosHint}</span>
            </header>
            {repeatCombos.length === 0 ? (
                <div className="userStatsEmpty">
                    No repeat combos yet — every combo line so far has only shipped in one deck.
                </div>
            ) : (
                <ol className="userStatsComboList">
                    {repeatCombos.map((c, idx) => {
                        const type = (c.type || '').toUpperCase();
                        const tone = type === 'WINCON' ? 'alert' : type === 'LOCK' ? 'cool' : 'gold';
                        // Prefer API-provided `cardNames` if present, else fall
                        // back to title-casing the slug ids for a readable
                        // display string.
                        const names =
                            Array.isArray(c.cardNames) && c.cardNames.length
                                ? c.cardNames
                                : (c.cardIds || []).map(prettifyCardId);
                        const displayName = names.join(' + ') || prettifyCardId(c.id);
                        // Dedupe category tags — backend occasionally lists the
                        // same tag multiple times (e.g. `['mana', 'mana']`).
                        const uniqueCats = Array.from(new Set(c.categories || []));
                        return (
                            <li key={`${c.id || 'combo'}-${idx}`} className="userStatsComboRow">
                                <span className={`userStatsComboType userStatsComboType--${tone}`}>
                                    {type || 'Combo'}
                                </span>
                                <a
                                    className="userStatsComboLink"
                                    href={c.spellbookUri || '#'}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => {
                                        if (!c.spellbookUri) e.preventDefault();
                                    }}
                                >
                                    {displayName}
                                </a>
                                <span className="userStatsComboCount">{c.count}×</span>
                                {uniqueCats.length > 0 && (
                                    <div className="userStatsComboCats">
                                        {uniqueCats.slice(0, 4).map((cat) => (
                                            <span key={cat} className="userStatsComboCat">
                                                {cat.replace(/^infinite-/, '').replace(/-/g, ' ')}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ol>
            )}
        </section>
    );
}

// ---------- Top commanders ------------------------------------------------

function commanderNameToSlug(name) {
    return String(name || '')
        .toLowerCase()
        .replace(/['`\u2018\u2019,]/g, '')
        .replace(/[\s\-/]+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
}

function prettifyCommanderSlug(slug) {
    if (!slug) return '';
    return String(slug)
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function TopCommandersSection({ commanders, voice }) {
    const dispatch = useDispatch();
    const listItems = useSelector((state) => state?.userDetails?.listItems);

    // The /usercardstats projection sometimes returns `commanderNames: []`.
    // Fall back to the loaded deck list — each deck carries real commander
    // names we can index by a slug derived from those names, matching the
    // shape the backend uses for `commanderIndexId`.
    const nameBySlug = useMemo(() => {
        const map = new Map();
        (listItems || []).forEach((item) => {
            const names = Array.isArray(item?.commanders) ? item.commanders.filter(Boolean) : [];
            if (names.length === 0) return;
            const joinedSlug = names.map(commanderNameToSlug).filter(Boolean).join('_');
            if (joinedSlug && !map.has(joinedSlug)) map.set(joinedSlug, names);
        });
        return map;
    }, [listItems]);

    const resolveDisplay = (c) => {
        const apiNames = Array.isArray(c?.commanderNames) ? c.commanderNames : [];
        if (apiNames.length > 0) return apiNames.join(' + ');
        const cached = nameBySlug.get(c?.commanderIndexId);
        if (cached && cached.length > 0) return cached.join(' + ');
        const ids =
            Array.isArray(c?.commanderIds) && c.commanderIds.length > 0
                ? c.commanderIds
                : [c?.commanderIndexId].filter(Boolean);
        return ids.map(prettifyCommanderSlug).filter(Boolean).join(' + ') || c?.commanderIndexId || '';
    };

    const repeatCommanders = (commanders || []).filter((c) => Number(c?.count) > 1);

    return (
        <section className="userStatsSection userStatsCommanders">
            <header className="userStatsSectionHeader">
                <span className="userStatsSectionEyebrow">Top commanders</span>
                <span className="userStatsSectionHint">{voice.commandersHint}</span>
            </header>
            {repeatCommanders.length === 0 ? (
                <div className="userStatsEmpty">No repeat commanders yet — every deck so far uses a different one.</div>
            ) : (
                <ol className="userStatsCommanderList">
                    {repeatCommanders.map((c, idx) => {
                        const display = resolveDisplay(c);
                        const ratio = c.deckRatio != null ? formatRatio(c.deckRatio) : null;
                        const handleClick = () => {
                            if (c.commanderIndexId) {
                                dispatch(
                                    setAppRoute({
                                        route: `/commanders/id/${c.commanderIndexId}`,
                                    })
                                );
                            }
                        };
                        return (
                            <li key={c.commanderIndexId || idx} className="userStatsCommanderRow">
                                <span className="userStatsCommanderRank">{idx + 1}</span>
                                <button
                                    type="button"
                                    className="userStatsCommanderBtn searedBtn"
                                    onClick={handleClick}
                                    disabled={!c.commanderIndexId}
                                >
                                    <span className="userStatsCommanderName">{display}</span>
                                </button>
                                <span className="userStatsCommanderCount">
                                    {c.count} deck{c.count === 1 ? '' : 's'}
                                    {ratio ? ` · ${ratio}` : ''}
                                </span>
                            </li>
                        );
                    })}
                </ol>
            )}
        </section>
    );
}

// ---------- Color profile (new) ------------------------------------------

function ColorProfileSection({ combinations, perColor, deckCount, voice }) {
    if ((!combinations || combinations.length === 0) && !perColor) return null;
    const maxPerColor = Math.max(1, ...Object.values(perColor || {}).map(Number));
    // Mono-color identities are already shown in the per-color breakdown to
    // the right — filter them out of the combinations list so this column
    // focuses on multi-color identities.
    const multiColorCombos = (combinations || []).filter((row) => (row?.colors || '').length > 1);
    const maxComboRatio = multiColorCombos.reduce((m, row) => Math.max(m, Number(row.deckRatio || 0)), 0);
    return (
        <section className="userStatsSection userStatsColors">
            <header className="userStatsSectionHeader">
                <span className="userStatsSectionEyebrow">Color mix</span>
                <span className="userStatsSectionHint">{voice.colorsHint}</span>
            </header>
            <div className="userStatsColorSplit">
                <div className="userStatsColorCombos">
                    {multiColorCombos.slice(0, 8).map((row) => {
                        const ratio = Number(row.deckRatio || 0);
                        const width = maxComboRatio > 0 ? (ratio / maxComboRatio) * 100 : 0;
                        return (
                            <div key={row.colors || '—'} className="userStatsColorComboRow">
                                <div className="userStatsColorSwatches">
                                    {(row.colors || '').split('').map((c, i) => (
                                        <ManaSymbol key={`${c}_${i}`} color={c} />
                                    ))}
                                </div>
                                <span className="userStatsColorLabel">{row.colors || '—'}</span>
                                <div className="userStatsColorBar">
                                    <div
                                        className={`userStatsColorBarFill userStatsColorBarFill--${(
                                            row.colors || 'c'
                                        ).toLowerCase()}`}
                                        style={{ width: `${Math.max(4, width)}%` }}
                                    />
                                </div>
                                <span className="userStatsColorCount">
                                    {row.count} · {formatRatio(row.deckRatio)}
                                </span>
                            </div>
                        );
                    })}
                </div>
                <div className="userStatsColorPerColor">
                    <span className="userStatsColorPerColorLabel">Per color</span>
                    <div className="userStatsColorPerColorList">
                        {WUBRG.filter((c) => perColor?.[c] != null).map((c) => {
                            const v = Number(perColor?.[c] || 0);
                            const ratio = deckCount > 0 ? v / deckCount : 0;
                            return (
                                <div className="userStatsColorPerColorRow" key={c}>
                                    <ManaSymbol color={c} size="large" />
                                    <div className="userStatsColorPerColorBar">
                                        <div
                                            className={`userStatsColorPerColorBarFill userStatsColorPerColorBarFill--${c.toLowerCase()}`}
                                            style={{
                                                width: `${Math.max(4, (v / maxPerColor) * 100)}%`,
                                            }}
                                        />
                                    </div>
                                    <span className="userStatsColorPerColorValue">
                                        {v}
                                        <span className="userStatsColorPerColorRatio"> · {formatRatio(ratio)}</span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------- Archetype section --------------------------------------------

function ArchetypeSection({ majorArchetypes, minorArchetypes, topArchetypes, diversity, voice }) {
    const bucket = diversityBucket(diversity);
    const diversityPct = diversity == null ? 0 : Math.max(0, Math.min(1, diversity)) * 100;
    const majors = Array.isArray(majorArchetypes) ? majorArchetypes : [];
    const minors = Array.isArray(minorArchetypes) ? minorArchetypes : [];
    const tops = Array.isArray(topArchetypes) ? topArchetypes : [];
    return (
        <section className="userStatsSection">
            <header className="userStatsSectionHeader">
                <span className="userStatsSectionEyebrow">Archetype mix</span>
                <span className="userStatsSectionHint">{voice.archetypeHint}</span>
            </header>
            {majors.length > 0 && (
                <div className="userStatsMajorBreakdown">
                    <div className="synergyStackedBar">
                        {majors.map((m) => (
                            <div
                                key={m.label}
                                className="synergyStackedSegment"
                                style={{
                                    width: `${Math.max((Number(m.percent) || 0) * 100, 2)}%`,
                                    background: archetypeColor(m.label),
                                }}
                                title={`${prettifyArchetype(m.label)} — ${m.count} deck${m.count === 1 ? '' : 's'}`}
                            />
                        ))}
                    </div>
                    <div className="synergyStackedLegend">
                        {majors.map((m) => (
                            <div key={m.label} className="synergyStackedLegendItem">
                                <span
                                    className="synergyStackedSwatch"
                                    style={{ background: archetypeColor(m.label) }}
                                />
                                <span className="synergyStackedLegendLabel">{prettifyArchetype(m.label)}</span>
                                <span className="synergyStackedLegendValue">
                                    {Math.round((Number(m.percent) || 0) * 100)}%
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className={`userStatsDiversity userStatsDiversity--${bucket.tone}`}>
                <div className="userStatsDiversityLabelRow">
                    <span className="userStatsDiversityLabel">Archetype diversity</span>
                    <span className="userStatsDiversityValue">
                        {diversity == null ? '—' : diversity.toFixed(2)}
                        <span className="userStatsDiversityBucket">· {bucket.label}</span>
                    </span>
                </div>
                <div className="userStatsDiversityBar">
                    <div className="userStatsDiversityBarFill" style={{ width: `${diversityPct}%` }} />
                </div>
                <div className="userStatsDiversityScale">
                    <span>Narrow</span>
                    <span>Mixed</span>
                    <span>Broad</span>
                </div>
            </div>
            {tops.length > 0 && (
                <div className="userStatsArchetypeGroup">
                    <span className="userStatsArchetypeGroupLabel">Top combinations</span>
                    <div className="userStatsArchetypeChips">
                        {tops.map((a) => (
                            <span key={a.label} className="userStatsArchetypeChip">
                                <span className="userStatsArchetypeChipLabel">{prettifyArchetype(a.label)}</span>
                                <span className="userStatsArchetypeChipCount">
                                    {a.count}
                                    {a.percent != null ? ` · ${formatRatio(a.percent)}` : ''}
                                </span>
                            </span>
                        ))}
                    </div>
                </div>
            )}
            {minors.length > 0 && (
                <div className="userStatsArchetypeGroup">
                    <span className="userStatsArchetypeGroupLabel">Minor themes</span>
                    <div className="userStatsArchetypeChips">
                        {minors.map((m) => (
                            <span
                                key={m.label}
                                className="userStatsArchetypeChip userStatsArchetypeChip--minor"
                                style={{ borderLeftColor: archetypeColor(m.label) }}
                            >
                                <span className="userStatsArchetypeChipLabel">{prettifyArchetype(m.label)}</span>
                                <span className="userStatsArchetypeChipCount userStatsArchetypeChipCount--minor">
                                    {Math.round((Number(m.percent) || 0) * 100)}%
                                </span>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}

// ---------- Category roles / subs (new) ----------------------------------

const ROLE_ORDER = ['consistency', 'interaction', 'efficiency', 'winConditions'];
const ROLE_LABELS = {
    consistency: 'Consistency',
    interaction: 'Interaction',
    efficiency: 'Efficiency',
    winConditions: 'Win conditions',
};

function CategoryRoleSection({ roles, subs }) {
    const roleEntries = roles
        ? ROLE_ORDER.filter((k) => roles[k] != null).map((k) => ({
              key: k,
              label: ROLE_LABELS[k] || k,
              value: Number(roles[k] || 0),
          }))
        : [];
    const subEntries = Array.isArray(subs) ? subs.slice(0, 16) : [];
    if (!roleEntries.length && !subEntries.length) return null;
    const maxRole = roleEntries.reduce((m, r) => Math.max(m, r.value), 1);
    const maxSub = subEntries.reduce((m, s) => Math.max(m, Number(s.avgPerDeck || 0)), 1);
    return (
        <section className="userStatsSection">
            <header className="userStatsSectionHeader">
                <span className="userStatsSectionEyebrow">Category mix</span>
                <span className="userStatsSectionHint">
                    Average cards per deck in each role, plus the leaf categories driving those totals.
                </span>
            </header>
            {roleEntries.length > 0 && (
                <div className="userStatsRoleGrid">
                    {roleEntries.map((r) => (
                        <div key={r.key} className={`userStatsRoleRow userStatsRoleRow--${r.key}`}>
                            <span className="userStatsRoleLabel">{r.label}</span>
                            <div className="userStatsRoleBar">
                                <div
                                    className="userStatsRoleBarFill"
                                    style={{
                                        width: `${Math.max(4, (r.value / maxRole) * 100)}%`,
                                    }}
                                />
                            </div>
                            <span className="userStatsRoleValue">
                                {r.value.toFixed(1)}
                                <span className="userStatsRoleValueUnit"> / deck</span>
                            </span>
                        </div>
                    ))}
                </div>
            )}
            {subEntries.length > 0 && (
                <div className="userStatsSubGrid">
                    {subEntries.map((s) => {
                        const v = Number(s.avgPerDeck || 0);
                        return (
                            <div key={s.sub} className="userStatsSubTile">
                                <span className="userStatsSubLabel">{(s.sub || '').replace(/[_-]/g, ' ')}</span>
                                <span className="userStatsSubValue">{v.toFixed(1)}</span>
                                <div className="userStatsSubBar">
                                    <div
                                        className="userStatsSubBarFill"
                                        style={{
                                            width: `${Math.max(4, (v / maxSub) * 100)}%`,
                                        }}
                                    />
                                </div>
                                {s.totalAcrossDecks != null && (
                                    <span className="userStatsSubTotal">{s.totalAcrossDecks} total</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

// ---------- Aggregate stats ----------------------------------------------

function formatLastUpdated(ts) {
    if (!ts) return null;
    const d = new Date(Number(ts));
    if (Number.isNaN(d.getTime())) return null;
    const diff = Date.now() - d.getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
}

function AggregateStats({ aggregate, projection, lastUpdatedAt, voice }) {
    const pick = (key, fallback) => {
        const fromAgg = aggregate?.[key];
        if (fromAgg != null) return fromAgg;
        return fallback;
    };
    const manabase = projection?.manabase || {};
    const combos = projection?.combos || {};
    const synergy = projection?.synergy || {};

    // Power / bracket / salt / synergy averages are already rendered in the
    // profile hero above — omit them here to avoid duplication. The tiles
    // below focus on deck-construction signals (manabase, combos, synergy
    // ceiling) that the hero doesn't surface.
    const tiles = [
        {
            key: 'avgCMC',
            label: 'Avg CMC',
            value: pick('avgCMC', manabase.avgCMC),
            fmt: (v) => v?.toFixed(2),
        },
        {
            key: 'avgLandCount',
            label: 'Avg lands',
            value: pick('avgLandCount', manabase.avgLandCount),
            fmt: (v) => v?.toFixed(1),
        },
        {
            key: 'avgManaSources',
            label: 'Mana sources',
            value: pick('avgManaSources', manabase.avgManaSources),
            fmt: (v) => v?.toFixed(1),
        },
        {
            key: 'avgManaFixing',
            label: 'Mana fixing',
            value: pick('avgManaFixing', manabase.avgManaFixing),
            fmt: (v) => v?.toFixed(0),
        },
        {
            key: 'avgFastMana',
            label: 'Fast mana / deck',
            value: pick('avgFastMana', manabase.avgFastMana),
            fmt: (v) => v?.toFixed(1),
        },
        {
            key: 'avgTapLands',
            label: 'Tap lands / deck',
            value: pick('avgTapLands', manabase.avgTapLands),
            fmt: (v) => v?.toFixed(1),
        },
        {
            key: 'comboDeckRatio',
            label: 'Combo decks',
            value: pick('comboDeckRatio', combos.comboDeckRatio),
            fmt: formatRatio,
        },
        {
            key: 'winconComboRatio',
            label: 'Wincon combos',
            value: pick('winconComboRatio', combos.winconComboRatio),
            fmt: formatRatio,
        },
        {
            key: 'avgComboInstances',
            label: 'Combos / deck',
            value: pick('avgComboInstances', combos.avgComboInstances),
            fmt: (v) => v?.toFixed(1),
        },
        {
            key: 'maxSynergyScore',
            label: 'Max synergy',
            value: pick('maxSynergyScore', synergy.maxScore),
            fmt: (v) => v?.toFixed(0),
        },
    ].filter((t) => t.value != null && !Number.isNaN(Number(t.value)));

    if (!tiles.length) return null;
    const updated = formatLastUpdated(lastUpdatedAt);

    return (
        <section className="userStatsSection">
            <header className="userStatsSectionHeader">
                <span className="userStatsSectionEyebrow">Aggregate deck stats</span>
                <span className="userStatsSectionHint">
                    {voice.aggregateHint}
                    {updated ? ` Last refreshed ${updated}.` : ''}
                </span>
            </header>
            <div className="userStatsAggregateGrid">
                {tiles.map((t) => (
                    <div key={t.key} className="userStatsAggregateTile">
                        <span className="userStatsAggregateLabel">{t.label}</span>
                        <span className="userStatsAggregateValue">{t.fmt(Number(t.value))}</span>
                    </div>
                ))}
            </div>
        </section>
    );
}
